/**
 * CrmShareCard — Renders a shared CRM entity (lead, task, project, deal) in team chat
 *
 * Clicking the card title opens:
 * - Lead: UnifiedDetailsPage in ResponsiveModal
 * - Deal: dispatches 'open-deal-drawer' event (handled by Layout)
 * - Project: ProjectDetailsModal
 * - Task: navigates to /projects/:id?task=:id (TaskDrawer needs project context)
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, CheckSquare, FolderKanban, Receipt, ExternalLink } from 'lucide-react';
import { ResponsiveModal } from '../ui/ResponsiveModal';
import { UnifiedDetailsPage } from '../../pages/leads/LeadDetailsPage';
import { ProjectDetailsModal } from '../projects/ProjectDetailsModal';

interface CrmShareData {
  entity_type: 'lead' | 'task' | 'project' | 'deal';
  entity_id: string;
  entity_data: Record<string, unknown>;
  note?: string | null;
}

const entityConfig: Record<string, { label: string; Icon: React.FC<any>; color: string; bg: string; border: string }> = {
  lead: { label: 'Lead', Icon: UserPlus, color: '#EA580C', bg: 'rgba(234,88,12,0.06)', border: 'rgba(234,88,12,0.25)' },
  task: { label: 'Task', Icon: CheckSquare, color: '#7C3AED', bg: 'rgba(124,58,237,0.06)', border: 'rgba(124,58,237,0.25)' },
  project: { label: 'Progetto', Icon: FolderKanban, color: '#16A34A', bg: 'rgba(22,163,74,0.06)', border: 'rgba(22,163,74,0.25)' },
  deal: { label: 'Offerta', Icon: Receipt, color: '#2383E2', bg: 'rgba(35,131,226,0.06)', border: 'rgba(35,131,226,0.25)' },
};

const statusLabels: Record<string, string> = {
  todo: 'Da fare', in_progress: 'In corso', completed: 'Completata', review: 'In revisione', blocked: 'Bloccata',
  planning: 'Pianificazione', active: 'Attivo', on_hold: 'In pausa', cancelled: 'Annullato',
  draft: 'Bozza', sent: 'Inviata', accepted: 'Accettata', rejected: 'Rifiutata',
};

const priorityConfig: Record<string, { label: string; color: string }> = {
  low: { label: 'Bassa', color: '#16A34A' },
  medium: { label: 'Media', color: '#D97706' },
  high: { label: 'Alta', color: '#EA580C' },
  urgent: { label: 'Urgente', color: '#DC2626' },
};

function LeadFields({ data }: { data: Record<string, unknown> }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px]" style={{ color: '#787774' }}>
      {data.email && <span>{data.email as string}</span>}
      {data.phone && <span>{data.phone as string}</span>}
      {data.source && <span>Fonte: {data.source as string}</span>}
      {data.stage && <span>Stage: {data.stage as string}</span>}
    </div>
  );
}

function TaskFields({ data }: { data: Record<string, unknown> }) {
  const priority = data.priority as string | undefined;
  const pConfig = priority ? priorityConfig[priority] : null;
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]" style={{ color: '#787774' }}>
      {data.project_name && <span>{data.project_name as string}</span>}
      {data.status && <span>{statusLabels[data.status as string] || (data.status as string)}</span>}
      {pConfig && <span style={{ color: pConfig.color }}>{pConfig.label}</span>}
      {data.assignee && <span>{data.assignee as string}</span>}
    </div>
  );
}

function ProjectFields({ data }: { data: Record<string, unknown> }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]" style={{ color: '#787774' }}>
      {data.client_name && <span>{data.client_name as string}</span>}
      {data.status && <span>{statusLabels[data.status as string] || (data.status as string)}</span>}
      {data.due_date && <span>Scadenza: {new Date(data.due_date as string).toLocaleDateString('it-IT')}</span>}
      {data.pm && <span>PM: {data.pm as string}</span>}
    </div>
  );
}

function DealFields({ data }: { data: Record<string, unknown> }) {
  const amount = data.amount ? `€${Number(data.amount).toLocaleString('it-IT', { minimumFractionDigits: 2 })}` : null;
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]" style={{ color: '#787774' }}>
      {data.client_name && <span>{data.client_name as string}</span>}
      {amount && <span className="font-semibold" style={{ color: '#37352F' }}>{amount}</span>}
      {data.status && <span>{statusLabels[data.status as string] || (data.status as string)}</span>}
      {data.quote_number && <span>#{data.quote_number as string}</span>}
    </div>
  );
}

const fieldComponents: Record<string, React.FC<{ data: Record<string, unknown> }>> = {
  lead: LeadFields,
  task: TaskFields,
  project: ProjectFields,
  deal: DealFields,
};

export const CrmShareCard: React.FC<{ content: string }> = ({ content }) => {
  const navigate = useNavigate();
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);

  let data: CrmShareData | null = null;
  try {
    const obj = JSON.parse(content);
    if (obj && obj.entity_type && obj.entity_id) data = obj;
  } catch { /* fallback */ }

  if (!data) {
    return (
      <div className="text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: '#37352F' }}>
        {content}
      </div>
    );
  }

  const config = entityConfig[data.entity_type] || entityConfig.lead;
  const { Icon } = config;
  const FieldsComponent = fieldComponents[data.entity_type];
  const entityTitle = (data.entity_data.name || data.entity_data.title || config.label) as string;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    switch (data!.entity_type) {
      case 'lead':
        setShowLeadModal(true);
        break;
      case 'deal':
        window.dispatchEvent(new CustomEvent('open-deal-drawer', { detail: { entityId: data!.entity_id } }));
        break;
      case 'project':
        setShowProjectModal(true);
        break;
      case 'task': {
        const projectId = data!.entity_data.project_id;
        if (projectId) {
          navigate(`/projects/${projectId}?task=${data!.entity_id}`);
        } else {
          navigate(`/projects?task=${data!.entity_id}`);
        }
        break;
      }
    }
  };

  return (
    <>
      <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${config.border}`, backgroundColor: config.bg }}>
        {/* Header */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium" style={{ backgroundColor: `${config.color}15`, color: config.color }}>
          <Icon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.5} />
          {config.label} condiviso
        </div>

        <div className="px-3 py-2 space-y-1.5">
          {/* Title — click opens modal */}
          <button
            onClick={handleClick}
            className="text-[13px] font-semibold leading-snug flex items-center gap-1 hover:underline text-left"
            style={{ color: '#37352F' }}
          >
            {entityTitle}
            <ExternalLink className="w-3 h-3 flex-shrink-0" style={{ color: '#9B9A97' }} strokeWidth={1.5} />
          </button>

          {/* Entity-specific fields */}
          {FieldsComponent && <FieldsComponent data={data.entity_data} />}

          {/* Note */}
          {data.note && (
            <div className="text-[12px] leading-relaxed mt-1 pt-1.5 whitespace-pre-wrap" style={{ color: '#37352F', borderTop: `1px solid ${config.border}` }}>
              {data.note}
            </div>
          )}
        </div>
      </div>

      {/* Lead modal */}
      {showLeadModal && data.entity_type === 'lead' && (
        <ResponsiveModal
          isOpen={true}
          onClose={() => setShowLeadModal(false)}
          title="Dettaglio Lead"
          size="wide"
          mobileHeight="full"
        >
          <UnifiedDetailsPage
            leadId={data.entity_id}
            isModal={true}
            onClose={() => setShowLeadModal(false)}
          />
        </ResponsiveModal>
      )}

      {/* Project modal */}
      {showProjectModal && data.entity_type === 'project' && (
        <ProjectDetailsModal
          projectId={data.entity_id}
          isOpen={true}
          onClose={() => setShowProjectModal(false)}
        />
      )}
    </>
  );
};

export default CrmShareCard;
